import AppContext from 'App/AppContext';
import { IPFontIcon, IPLabel, IPLink, IPSpinner, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { ApprovalButtonApprove, ApprovalButtonReject } from 'components/Buttons/ApprovalButton';
import { LinkPreviewModalOrUrl } from 'components/Links/Preview/LinkPreviewModalOrUrl';
import RichTextReadOnly from 'components/Text/RichTextReadOnly';
import { globalStackTokensGapExtraSmall, globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import { ApprovalState } from 'models/approval';
import ResourceLink from 'models/resourceLink';
import Task from 'models/tasks/task';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetLink } from 'services/Api/linkService';
import { apiRequest } from 'services/Auth/authConfig';

interface IApprovalTaskDetailItems {
  task: Task;
  compact?: boolean;
  navigateExternal?: boolean;
  onUpdate: (newTask: Task) => void;
}

export const ApprovalTaskDetailItems = (props: IApprovalTaskDetailItems) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['library', 'translation']);
  const [approvalItemLoading, setApprovalItemLoading] = useState<boolean>(false);
  const [approvalItem, setApprovalItem] = useState<ResourceLink | undefined>(undefined);
  const [currentTaskId, setCurrentTaskId] = useState<number | undefined>(undefined);
  const [showApprovalItem, setShowApprovalItem] = useState<boolean>(false);
  const iconFontSize = { fontSize: '20px', marginTop: '5px' };

  useEffect(() => {
    if (props.task.taskId !== currentTaskId || !approvalItem) {
      setCurrentTaskId(props.task.taskId);
      loadApprovalItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.task]);

  const loadApprovalItem = async () => {
    try {
      if (approvalItemLoading || !props.task.resourceLinkIds || props.task.resourceLinkIds?.length === 0) return;
      setShowApprovalItem(false);
      setApprovalItemLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const linkId = props.task.resourceLinkIds[0];
      const link = await apiGetLink(linkId, accessToken, appContext.globalDataCache);
      setApprovalItem(link);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setApprovalItemLoading(false);
    }
  };

  const isApprovedOrRejected = (): boolean => {
    return props.task.approved !== ApprovalState.Pending;
  };

  //
  // Main render
  //
  return (
    <IPStack tokens={globalStackTokensGapSmall}>
      <IPStack.Item>
        <IPStack
          horizontal
          tokens={appContext.isMobileView ? globalStackTokensGapExtraSmall : globalStackTokensGapMedium}
          verticalAlign="start"
        >
          <IPStack.Item>
            {!appContext.isMobileView && <IPFontIcon iconName="DocumentSet" style={iconFontSize} />}
          </IPStack.Item>
          <IPStack.Item>
            <IPLabel>{t('library:ApprovalTasks.ApprovalTaskDetail.ItemToApprove')}</IPLabel>
            {approvalItemLoading && <IPSpinner />}
            {!approvalItemLoading && (
              <IPLink
                onClick={() => {
                  setShowApprovalItem(true);
                }}
              >
                {approvalItem?.linkName}
              </IPLink>
            )}
            {!approvalItemLoading && !approvalItem && (
              <IPText>{t('library:ApprovalTasks.ApprovalTaskDetail.ItemToApproveEmpty')}</IPText>
            )}
            <LinkPreviewModalOrUrl
              isOpen={showApprovalItem}
              onClose={() => {
                setShowApprovalItem(false);
              }}
              link={approvalItem}
              confirmOpen={props.navigateExternal}
              enableLinks={true}
            />
          </IPStack.Item>
        </IPStack>
      </IPStack.Item>
      <IPStack.Item>
        <IPStack
          horizontal
          tokens={appContext.isMobileView ? globalStackTokensGapExtraSmall : globalStackTokensGapMedium}
          verticalAlign="start"
        >
          <IPStack.Item>
            {!appContext.isMobileView && <IPFontIcon iconName="TextDocument" style={iconFontSize} />}
          </IPStack.Item>
          <IPStack.Item grow>
            <IPLabel>{t('library:ApprovalTasks.ApprovalTaskDetail.Instruction')}</IPLabel>
            <RichTextReadOnly
              stripParagraph
              html={props.task.description || ''}
              placeholder={t('library:ApprovalTasks.ApprovalTaskDetail.InstructionEmpty')}
            />
          </IPStack.Item>
        </IPStack>
      </IPStack.Item>
      <IPStack.Item>
        <IPStack
          horizontal
          tokens={appContext.isMobileView ? globalStackTokensGapExtraSmall : globalStackTokensGapMedium}
          verticalAlign="start"
          styles={{ root: { paddingTop: 10 } }}
        >
          <IPStack.Item>
            {!appContext.isMobileView && <IPFontIcon iconName="FavoriteStarFill" style={iconFontSize} />}
          </IPStack.Item>
          <IPStack.Item grow>
            <IPStack
              horizontal
              wrap
              tokens={globalStackTokensGapSmall}
              verticalAlign="center"
              styles={{ root: { paddingRight: 5 } }}
            >
              <IPStack.Item>
                <ApprovalButtonApprove
                  compact={props.compact}
                  selected={props.task.approved === ApprovalState.Approved}
                  disabled={appContext.user.id !== props.task.userId}
                  onClick={() => {
                    const copyTask = props.task.clone();
                    copyTask.approved = ApprovalState.Approved;
                    props.onUpdate(copyTask);
                  }}
                />
              </IPStack.Item>
              <IPStack.Item>
                <ApprovalButtonReject
                  compact={props.compact}
                  selected={props.task.approved === ApprovalState.Rejected}
                  disabled={appContext.user.id !== props.task.userId}
                  onClick={() => {
                    const copyTask = props.task.clone();
                    copyTask.approved = ApprovalState.Rejected;
                    props.onUpdate(copyTask);
                  }}
                />
              </IPStack.Item>
              <IPStack.Item>
                <IPLink
                  disabled={appContext.user.id !== props.task.userId || !isApprovedOrRejected()}
                  onClick={() => {
                    const copyTask = props.task.clone();
                    copyTask.approved = ApprovalState.Pending;
                    copyTask.taskStateId = copyTask.getFirstState();
                    props.onUpdate(copyTask);
                  }}
                >
                  {t('library:ApprovalTasks.ApprovalTaskDetail.ResetAnswer')}
                </IPLink>
              </IPStack.Item>
            </IPStack>
          </IPStack.Item>
        </IPStack>
      </IPStack.Item>
    </IPStack>
  );
};
